import { useRef, useEffect, useState } from 'react';
import { useAppSelector } from 'src/store/store';

interface UseDualStreamCaptureReturn {
  lowResVideoRef: React.RefObject<HTMLVideoElement>;
  highResVideoRef: React.RefObject<HTMLVideoElement>;
  captureHighResFrame: () => Promise<File>;
  captureLowResFrame: () => string | null;
  streamsReady: boolean;
  isDualMode: boolean;
}

/**
 * Hook to manage dual camera streams with single camera fallback.
 * 
 * Attempts to maintain two simultaneous video streams using screen-based resolution:
 * - Low-res (50% of screen) for continuous scanning
 * - High-res (100% of screen) for capture
 * 
 * Falls back to single camera with quality control if dual streams fail.
 */
export const useDualStreamCapture = (
  lowResScale: number = 0.5,
  highResScale: number = 1.0
): UseDualStreamCaptureReturn => {
  const lowResVideoRef = useRef<HTMLVideoElement>(null);
  const highResVideoRef = useRef<HTMLVideoElement>(null);
  const [streamsReady, setStreamsReady] = useState(false);
  const [isDualMode, setIsDualMode] = useState(false);

  // Store the actual MediaStream objects so we can stop them even if srcObject is cleared
  const lowResStreamRef = useRef<MediaStream | null>(null);
  const highResStreamRef = useRef<MediaStream | null>(null);

  // Persistent canvases for frame capture - reused to prevent flash
  const lowResCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const lowResContextRef = useRef<CanvasRenderingContext2D | null>(null);
  const highResCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const highResContextRef = useRef<CanvasRenderingContext2D | null>(null);
  
  // Read screen dimensions and selected camera from Redux state (single source of truth)
  const { screenWidth, screenHeight, selectedCameraDeviceId } = useAppSelector((state) => state.inspectionCapture);

  useEffect(() => {
    let lowResStream: MediaStream | null = null;
    let highResStream: MediaStream | null = null;
    let mounted = true;
    async function initializeStreams() {
      // Use screen dimensions from Redux state with fallback
      const baseWidth = screenWidth || 2560;
      const baseHeight = screenHeight || 1440;
      
      const lowResWidth = Math.floor(baseWidth * lowResScale);
      const lowResHeight = Math.floor(baseHeight * lowResScale);
      const highResWidth = Math.floor(baseWidth * highResScale);
      const highResHeight = Math.floor(baseHeight * highResScale);

      // Build video constraints with deviceId if specified
      const buildVideoConstraints = (width: number, height: number): MediaTrackConstraints => {
        const constraints: MediaTrackConstraints = {
          width: { ideal: width },
          height: { ideal: height },
        };
        
        // Use deviceId if specified, otherwise use facingMode
        if (selectedCameraDeviceId) {
          constraints.deviceId = { exact: selectedCameraDeviceId };
        } else {
          constraints.facingMode = 'environment';
        }
        
        return constraints;
      };

      try {
        // Try dual camera approach first
        lowResStream = await navigator.mediaDevices.getUserMedia({
          video: buildVideoConstraints(lowResWidth, lowResHeight)
        });

        if (!mounted) {
          lowResStream.getTracks().forEach(track => track.stop());
          return;
        }

        highResStream = await navigator.mediaDevices.getUserMedia({
          video: buildVideoConstraints(highResWidth, highResHeight)
        });

        if (!mounted) {
          lowResStream.getTracks().forEach(track => track.stop());
          highResStream.getTracks().forEach(track => track.stop());
          return;
        }

        // Store streams in refs for cleanup
        lowResStreamRef.current = lowResStream;
        highResStreamRef.current = highResStream;

        // Attach streams to video elements
        if (lowResVideoRef.current) {
          lowResVideoRef.current.srcObject = lowResStream;
          await lowResVideoRef.current.play();
        }

        if (highResVideoRef.current) {
          highResVideoRef.current.srcObject = highResStream;
          await highResVideoRef.current.play();
        }

        setIsDualMode(true);
        setStreamsReady(true);
      } catch (error) {
        console.warn('⚠️ Dual camera initialization failed, using single camera fallback:', error);
        
        // Cleanup any partially initialized streams
        if (lowResStream) {
          lowResStream.getTracks().forEach(track => track.stop());
        }
        if (highResStream) {
          highResStream.getTracks().forEach(track => track.stop());
        }

        if (!mounted) {
          return;
        }

        // Fallback: Single camera at high resolution
        try {
          const singleStream = await navigator.mediaDevices.getUserMedia({
            video: buildVideoConstraints(highResWidth, highResHeight)
          });

          if (!mounted) {
            singleStream.getTracks().forEach(track => track.stop());
            return;
          }

          // Store single stream in both refs
          lowResStreamRef.current = singleStream;
          highResStreamRef.current = singleStream;
          
          // Use same stream for both refs in fallback mode
          if (lowResVideoRef.current) {
            lowResVideoRef.current.srcObject = singleStream;
            await lowResVideoRef.current.play();
          }
          
          if (highResVideoRef.current) {
            highResVideoRef.current.srcObject = singleStream;
            await highResVideoRef.current.play();
          }
          
          setIsDualMode(false);
          setStreamsReady(true);
          
        } catch (fallbackError) {
          console.error('❌ Failed to initialize camera (both dual and single):', fallbackError);
          setStreamsReady(false);
        }
      }
    }

    initializeStreams();

    // Cleanup: Stop all streams on unmount
    return () => {
      mounted = false;
      // Stop streams using refs (more reliable than checking video elements)
      if (lowResStreamRef.current) {
        lowResStreamRef.current.getTracks().forEach(track => {
          track.stop();
        });
        lowResStreamRef.current = null;
      }

      if (highResStreamRef.current) {
        highResStreamRef.current.getTracks().forEach(track => {
          track.stop();
        });
        highResStreamRef.current = null;
      }

      // Also clear video element srcObjects
      if (lowResVideoRef.current) {
        lowResVideoRef.current.srcObject = null;
      }
      if (highResVideoRef.current) {
        highResVideoRef.current.srcObject = null;
      }
    };
  }, [lowResScale, highResScale]);
  // Note: screenWidth and screenHeight intentionally NOT in dependencies
  // They are only used during initialization and should not trigger camera restart

  /**
   * Capture frame from low-res stream as base64 JPEG
   * Used for continuous WebSocket streaming
   * Uses persistent canvas to prevent flash
   */
  const captureLowResFrame = (): string | null => {
    if (!lowResVideoRef.current) return null;
    
    const video = lowResVideoRef.current;
    if (video.videoWidth === 0 || video.videoHeight === 0) return null;
    
    // Create canvas once and reuse (prevents flash)
    if (!lowResCanvasRef.current) {
      lowResCanvasRef.current = document.createElement('canvas');
      lowResContextRef.current = lowResCanvasRef.current.getContext('2d');
    }
    
    const canvas = lowResCanvasRef.current;
    const ctx = lowResContextRef.current;
    if (!ctx) return null;
    
    // Only resize if dimensions changed
    if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
    }
    
    ctx.drawImage(video, 0, 0);
    
    // Lower quality for streaming efficiency
    return canvas.toDataURL('image/jpeg', 0.6);
  };

  /**
   * Capture frame from high-res stream as File
   * Used for final photo capture (manual or auto)
   * Uses persistent canvas to prevent flash
   */
  const captureHighResFrame = async (): Promise<File> => {
    const video = highResVideoRef.current;
    if (!video) {
      throw new Error('High-res video stream not available');
    }
    
    // Create canvas once and reuse (prevents flash)
    if (!highResCanvasRef.current) {
      highResCanvasRef.current = document.createElement('canvas');
      highResContextRef.current = highResCanvasRef.current.getContext('2d');
    }
    
    const canvas = highResCanvasRef.current;
    const ctx = highResContextRef.current;
    if (!ctx) {
      throw new Error('Canvas context not available');
    }
    
    // Only resize if dimensions changed
    if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
    }
    
    ctx.drawImage(video, 0, 0);
    
    // Convert to high-quality JPEG blob
    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (b) => {
          if (b) {
            resolve(b);
          } else {
            reject(new Error('Failed to create blob from canvas'));
          }
        },
        'image/jpeg',
        0.95 // High quality for final capture
      );
    });
    
    return new File([blob], `capture_${Date.now()}.jpg`, { 
      type: 'image/jpeg' 
    });
  };

  return {
    lowResVideoRef,
    highResVideoRef,
    captureHighResFrame,
    captureLowResFrame,
    streamsReady,
    isDualMode
  };
};
